import { useRestApiProvider } from "@jugl-web/rest-api";
import { TaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { WithOptimistic } from "@jugl-web/utils";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Subject } from "rxjs";
import { UseTaskTimeLogListOptions } from "./types";
import { useTaskTimeLogListCache } from "./useTaskTimeLogListCache";

// Whether to show stale data while refetching
const STALE_WHILE_REVALIDATE = true;

const DEFAULT_PAGE_SIZE = 20;

export const taskTimeLogListUpdate$ = new Subject<
  | {
      type: "time-log-created";
      taskId: string;
      timeLog: TaskTimeLogModel;
    }
  | {
      type: "time-log-updated";
      taskId: string;
      timeLog: TaskTimeLogModel;
    }
  | {
      type: "time-log-deleted";
      taskId: string;
      timeLogId: string;
    }
>();

/**
 * Hook to work with time logs for a given task. Handles data fetching, pagination, and filtering.
 * It also exposes a setter to allow for local updates for already fetched time logs.
 */
export const useTaskTimeLogList = ({
  entityId,
  taskId,
  assigneeIds = [],
  pageSize = DEFAULT_PAGE_SIZE,
}: UseTaskTimeLogListOptions) => {
  const { tasksApi } = useRestApiProvider();

  const [getTimeLogs] = tasksApi.useLazyGetTaskTimeLogsQuery();

  const { getCachedTimeLogs, setCachedTimeLogs } = useTaskTimeLogListCache({
    entityId,
    taskId,
    assigneeIds,
  });

  const [combinedTimeLogs, setCombinedTimeLogs] = useState<
    WithOptimistic<TaskTimeLogModel>[]
  >(() => getCachedTimeLogs() ?? []);

  const combinedTimeLogIds = useMemo(
    () => new Set(combinedTimeLogs.map((timeLog) => timeLog.id)),
    [combinedTimeLogs]
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Track previous filter values to detect changes
  const previousFiltersRef = useRef({
    entityId,
    taskId,
    assigneeIds: assigneeIds.join(","),
  });

  const isInitializedRef = useRef(false);

  const fetchPage = useCallback(
    async (params: { page: number; assigneeIds: string[] }): Promise<void> => {
      setIsFetching(true);

      const response = await getTimeLogs(
        {
          entityId,
          taskId,
          params: {
            page: params.page,
            page_size: pageSize,
            users:
              params.assigneeIds.length > 0 ? params.assigneeIds : undefined,
          },
        },
        false
      );

      setIsFetching(false);

      if (!response?.data) {
        return;
      }

      const responseData = response.data;

      setTotalCount(responseData.total_entries);
      setHasMore(responseData.total_pages > params.page);

      if (params.page === 1) {
        // For page 1, replace all time logs
        setCombinedTimeLogs(responseData.data);
      } else {
        // For subsequent pages, merge with existing time logs
        setCombinedTimeLogs((prev) => {
          const getLogById = (id: string) =>
            responseData.data.find((log) => log.id === id);

          const updatedExisting = prev.map((log) => getLogById(log.id) ?? log);

          const existingIds = new Set(prev.map(({ id }) => id));

          const newLogs = responseData.data.filter(
            ({ id }) => !existingIds.has(id)
          );
          return [...updatedExisting, ...newLogs];
        });
      }
    },
    [getTimeLogs, entityId, taskId, pageSize]
  );

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setCurrentPage((previousPage) => previousPage + 1);
    }
  }, [isFetching, hasMore]);

  const refetchCurrentPage = useCallback(async () => {
    await fetchPage({
      page: currentPage,
      assigneeIds,
    });
  }, [currentPage, fetchPage, assigneeIds]);

  const resetPagination = useCallback(async () => {
    setCurrentPage(1);
    if (!STALE_WHILE_REVALIDATE) setCombinedTimeLogs([]);
    await fetchPage({ page: 1, assigneeIds });
  }, [fetchPage, assigneeIds]);

  const doesIncomingTimeLogMatchFilters = useCallback(
    (timeLog: TaskTimeLogModel) =>
      assigneeIds.length === 0 || assigneeIds.includes(timeLog.userId),
    [assigneeIds]
  );

  useLayoutEffect(() => {
    const prevFilters = previousFiltersRef.current;
    const currentAssigneeIdsStr = assigneeIds.join(",");

    // Check if any filter has changed
    const filtersChanged =
      prevFilters.entityId !== entityId ||
      prevFilters.taskId !== taskId ||
      prevFilters.assigneeIds !== currentAssigneeIdsStr;

    if (filtersChanged) {
      // Update the ref to current values
      previousFiltersRef.current = {
        entityId,
        taskId,
        assigneeIds: currentAssigneeIdsStr,
      };

      // Reset to page 1 and clear existing data
      setCurrentPage(1);
      if (!STALE_WHILE_REVALIDATE) setCombinedTimeLogs([]);
      setTotalCount(0);
      setHasMore(false);

      // Fetch page 1 with new filters
      fetchPage({ page: 1, assigneeIds });
    } else if (currentPage > 1) {
      // Filters haven't changed, but page has - fetch the new page
      fetchPage({ page: currentPage, assigneeIds });
    } else if (!isInitializedRef.current) {
      // Initial fetch
      isInitializedRef.current = true;
      fetchPage({ page: currentPage, assigneeIds });
    }
  }, [entityId, taskId, assigneeIds, currentPage, fetchPage]);

  // Persist time logs to cache every time it changes (due to either server or local updates)
  useEffect(() => {
    setCachedTimeLogs(combinedTimeLogs);
  }, [combinedTimeLogs, setCachedTimeLogs]);

  // Handle time log list updates from other parts of the app
  useEffect(() => {
    const subscription = taskTimeLogListUpdate$.subscribe((update) => {
      // Ignore updates for other tasks
      if (update.taskId !== taskId) {
        return;
      }

      switch (update.type) {
        case "time-log-created":
          if (doesIncomingTimeLogMatchFilters(update.timeLog)) {
            setCombinedTimeLogs((prev) => [...prev, update.timeLog]);
          }
          break;

        case "time-log-updated":
          setCombinedTimeLogs((prev) =>
            prev.map((timeLog) =>
              timeLog.id === update.timeLog.id ? update.timeLog : timeLog
            )
          );
          break;

        case "time-log-deleted":
          setCombinedTimeLogs((prev) =>
            prev.filter((timeLog) => timeLog.id !== update.timeLogId)
          );
          break;

        default:
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, [doesIncomingTimeLogMatchFilters, taskId]);

  return {
    timeLogs: combinedTimeLogs,
    timeLogIds: combinedTimeLogIds,
    isFetching,
    isLoading: isFetching && combinedTimeLogs.length === 0 && currentPage === 1,
    isLoadingMore: isFetching && currentPage > 1,
    currentPage,
    totalCount,
    hasMore,
    setTimeLogs: setCombinedTimeLogs,
    loadMore,
    refetchCurrentPage,
    resetPagination,
  };
};
